import type { ReactElement, ReactNode } from 'react';
import Link from 'next/link';
import clsx from 'clsx';

import { Alert, Box, Button, Flex, Text } from '@highlight-cp/core-ui';

import { SvgIcons } from '../svg-icons';
import classes from './info-cta.module.css';

export const INFO_CTA_ICONS = {
  LIGHTBULB: 'lightbulb',
  SKIP: 'skip',
};

type SvgIconsElement =
  | ReactElement<Parameters<typeof SvgIcons.Lightbulb>[0], typeof SvgIcons.Lightbulb>
  | ReactElement<Parameters<typeof SvgIcons.Skip>[0], typeof SvgIcons.Skip>;

interface InfoCtaProps {
  /**
   * The title of the info cta
   */
  title: string;
  /**
   * The description of the info cta
   */
  description: string;
  /**
   * The icon of the info cta (default: <SvgIcons.Lightbulb />)
   */
  svgIcon?: SvgIconsElement;
  /**
   * Action button props
   */
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
    target?: string;
  };
  /**
   * Custom button to render in the info cta
   */
  button?: ReactNode;
  /**
   * Cancel action button props
   */
  cancelAction?: {
    label: string;
    onClick?: () => void;
  };
  /**
   * Whether to display the info cta in a vertical layout
   */
  vertical?: boolean;
}

/**
 * Info CTA component
 * @description This is a more complex Info component that usually contains SVG based icons from Figma,
 * and an action button. For simple info alerts, just use the Info component from core-ui
 * @param title - The title of the info cta
 * @param description - The description of the info cta
 * @param icon - SvgIcons component to render as the icon, e.g. <SvgIcons.Skip />
 * @returns The info cta component
 */
const InfoCta = ({
  button,
  title,
  description,
  svgIcon = <SvgIcons.Lightbulb />,
  action,
  cancelAction,
  vertical = false,
}: InfoCtaProps) => {
  const actionButton =
    button ||
    (action?.href ? (
      <Button component={Link} href={action?.href} size="xs" target={action?.target}>
        {action?.label}
      </Button>
    ) : (
      <Button onClick={action?.onClick} size="xs">
        {action?.label}
      </Button>
    ));

  return (
    <Alert color="blue">
      <Flex align="center" gap="sm" direction={vertical ? 'column' : 'row'} justify="center">
        {svgIcon}
        <Box className={clsx(classes.text, vertical && classes.vertical)}>
          <Text fw={600}>{title}</Text>
          <Text>{description}</Text>
        </Box>
        {actionButton}
        {cancelAction && (
          <Button onClick={cancelAction.onClick} size="xs" variant="subtle">
            {cancelAction.label}
          </Button>
        )}
      </Flex>
    </Alert>
  );
};

InfoCta.ICONS = INFO_CTA_ICONS;

export default InfoCta;
